import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import Collapse from '@material-ui/core/Collapse';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import ReserveTime from '../../Common/Statistic/StatisticReserveTable/ReserveTime';
import ReserveMobileDetails from './ReserveMobileDetails';
import { TReserve } from '../../../types/IBookingStore';
import { TOrder } from '../../../types/IStatisticStore';
import locales from '../../../locales';
import { Routers } from '../../../config/constants';
import { parsePhoneNumber } from '../../../helpers/reserveHelpers';

const useStyles = makeStyles((theme) => ({
  card: {
    cursor: 'pointer',
    borderRadius: 8,
    border: '1px solid #E5E5E5',
    transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
      boxShadow: theme.shadows[4],
      borderColor: '#181F28',
    },
  },
  cardContent: {
    padding: theme.spacing(2),
    width: '100%',
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  mainInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
  },
  dateTime: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  name: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1.5),
    alignItems: 'start',
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  infoLabel: {
    fontSize: 12,
    color: '#AAAAAA',
    fontWeight: 500,
  },
  infoValue: {
    fontSize: 14,
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  link: {
    textDecoration: 'underline',
    color: theme.palette.text.primary,
  },
  error: {
    color: '#EA2727',
  },
  primary: {
    color: theme.palette.primary.main,
  },
  expandButton: {
    padding: theme.spacing(0.5),
    flexShrink: 0,
  },
  collapseContent: {
    paddingTop: theme.spacing(1),
    borderTop: '1px solid #E5E5E5',
  },
}));

interface Props {
  reserveInfo: TReserve;
  order: TOrder;
  handleOpenBookingDetails: (bookingId: number) => void;
  moneySymbol: string;
}

const ReserveMobileCard: React.FC<Props> = ({
  reserveInfo,
  order,
  handleOpenBookingDetails,
  moneySymbol,
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    if (!isOpen) {
      handleOpenBookingDetails(reserveInfo.id);
    }
    setIsOpen(!isOpen);
  };

  const stateColor = (state: string): 'initial' | 'primary' | 'error' => {
    switch (state) {
      case 'DECLINED':
      case 'GUEST_DECLINE':
      case 'RESTAURATEUR_DECLINE':
      case 'UNKNOWN_DECLINE':
        return 'error';
      case 'CLOSED':
        return 'primary';
      default:
        return 'initial';
    }
  };

  const statusColorClass = stateColor(reserveInfo.state) === 'error' 
    ? classes.error 
    : stateColor(reserveInfo.state) === 'primary' 
    ? classes.primary 
    : '';

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <div className={classes.mainInfo}>
            <div className={classes.dateTime}>
              <ReserveTime reserveInfo={reserveInfo} />
            </div>
            <Typography className={classes.name}>
              {reserveInfo.firstName}
            </Typography>
          </div>
          <IconButton 
            className={classes.expandButton}
            onClick={handleToggle}
            size="small"
          >
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>

        <Box className={classes.detailsGrid}>
          <Box className={classes.info}>
            <Typography className={classes.infoLabel}>
              {locales.t('statistics.reserves.columns.phone')}
            </Typography>
            <Typography className={classes.infoValue}>
              <Link 
                to={`${Routers.userStatistics}/${reserveInfo.phone}`}
                className={classes.link}
              >
                {parsePhoneNumber(reserveInfo.phone)}
              </Link>
            </Typography>
          </Box>

          <Box className={classes.info}>
            <Typography className={classes.infoLabel}>
              {locales.t('statistics.reserves.columns.status')}
            </Typography>
            <Typography className={classNames(classes.infoValue, statusColorClass)}>
              {locales.t(`booking.status.${reserveInfo.state}`)}
            </Typography>
          </Box>

          <Box className={classes.info}>
            <Typography className={classes.infoLabel}>
              {locales.t('statistics.reserves.columns.check')}
            </Typography>
            <Typography className={classes.infoValue}>
              {reserveInfo.receiptAmount 
                ? `${reserveInfo.receiptAmount} ${moneySymbol}` 
                : '—'}
            </Typography>
          </Box>

          <Box className={classes.info}>
            <Typography className={classes.infoLabel}>
              {locales.t('statistics.reserves.columns.source')}
            </Typography>
            <Typography className={classes.infoValue}>
              <Link 
                to={`${Routers.sourceStatistics}/${reserveInfo.source.id}`}
                className={classes.link}
              >
                {reserveInfo.source.name || '—'}
              </Link>
            </Typography>
          </Box>
        </Box>

        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <div className={classes.collapseContent}>
            <ReserveMobileDetails 
              moneySymbol={moneySymbol} 
              info={reserveInfo} 
              order={order} 
            />
          </div>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ReserveMobileCard;
